import { useQuery } from '@common/hooks';
import { Button } from 'antd';
import React, { useMemo, useState } from 'react';
import {
  Currency,
  DateRangeFilter,
  Link,
  RenderFilteredTable,
  RenderTableColumn,
  RenderTableOnChange,
  TextFilterSearch,
} from '@common/components';
import { toUSCompactDateFormat } from 'works/legacy/lib/date-helper';
import { useFilterCallbacks } from 'works/legacy/lib/hooks/use-filter-callbacks';
import {
  localStorageStore,
  useStoredFilters,
} from 'works/legacy/lib/hooks/use-stored-filters';
import { Spacing } from 'works/legacy/lib/style-helper';
import {
  INVOICE_LABEL_OVERWRITE,
  InvoiceState,
  ReverseInvoiceBulkActionFooter,
  TotalCountLabel,
} from '@pay-bill/components';
import {
  InvoiceStateType,
  PaginatedRevInvoiceArg,
  PaginatedRevInvoicesQuery,
  PaginatedRevInvoicesResponse,
  ReverseInvoiceListItemFragmentType,
  RevInvoiceFiltersQuery,
  RevInvoiceFiltersQueryType,
} from '@pay-bill/graphql';
import { CLEANUP_WHEN_EMPTY } from '@pay-bill/services';

type OrderField = PaginatedRevInvoiceArg['orderField'];

const defaultFilters: {
  states: InvoiceStateType[];
} = Object.freeze({
  states: ['open', 'paid'],
});

const defaultOrder: OrderField = 'CREATED_AT';

export const BillingReverseInvoiceList = ({
  hiddenFilters,
}: {
  hiddenFilters?: { worksProfile: string[] };
}) => {
  const [filters, setFilters] = useStoredFilters<
    PaginatedRevInvoiceArg['filters']
  >('RevInvoiceTable', {
    store: localStorageStore,
    defaults: defaultFilters,
  });
  const { onClearFilter, onClearAllFilters } = useFilterCallbacks(setFilters);

  const [pageData, setPageData] = useState(() => ({
    limit: 50,
    offset: 0,
    orderDirection: 'DESC' as PaginatedRevInvoiceArg['orderDirection'],
    orderField: defaultOrder as OrderField,
  }));

  const [selectedReverseInvoices, setSelectedReverseInvoices] = useState<
    ReverseInvoiceListItemFragmentType[]
  >([]);

  const { data: filterValues, loading: filtersLoading } =
    useQuery<RevInvoiceFiltersQueryType>(RevInvoiceFiltersQuery);
  const { data: reverseInvoices, loading } = useQuery<
    PaginatedRevInvoicesResponse,
    PaginatedRevInvoiceArg
  >(PaginatedRevInvoicesQuery, {
    notifyOnNetworkStatusChange: true,
    variables: {
      ...pageData,
      filters: {
        ...filters,
        ...hiddenFilters,
      },
    },
  });

  const reverseInvoicesPage = reverseInvoices?.page;
  const totalInvoices = reverseInvoicesPage?.totalCount ?? 0;

  const { organizationsFilter, statesFilter } = useMemo(() => {
    const { organizations = [], states = [] } = filterValues?.values || {};

    return {
      organizationsFilter: organizations,
      statesFilter: [...new Set([...states, ...defaultFilters.states])],
    };
  }, [filterValues?.values]);

  const revInvoiceColumns: RenderTableColumn<ReverseInvoiceListItemFragmentType>[] =
    [
      {
        title: 'Reverse Invoice #',
        key: 'code',
        width: 200,
        render: (_: unknown, revInvoice) => (
          <Link
            to={`/works/supplier/billing/reverse-invoices/${revInvoice.id}/summary`}
          >
            <b>{revInvoice.code}</b>
          </Link>
        ),
        sorter: true,
        filterFormat: 'id',
        filtered: !!filters.code?.length,
        filteredValue: filters.code || CLEANUP_WHEN_EMPTY,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              autoFocus
              placeHolder="Filter by code"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
      },
      {
        title: 'Date',
        dataIndex: 'createdAt',
        key: 'date',
        sorter: true,
        render: (createdAt) => toUSCompactDateFormat(createdAt),
        filterFormat: 'dateRange',
        filtered: !!filters.date?.length,
        filteredValue: filters.date || CLEANUP_WHEN_EMPTY,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <DateRangeFilter
              autoFocus
              onSearch={(value) => {
                setSelectedKeys((value || []) as string[]);
                confirm();
              }}
            />
          </section>
        ),
      },
      {
        title: 'Billing Amount',
        dataIndex: 'totalCents',
        key: 'amount',
        sorter: true,
        render: (totalCents) => <Currency value={totalCents / 100} />,
      },
      {
        title: 'Status',
        key: 'states',
        sorter: true,
        filtered: !!statesFilter.length,
        filteredValue: filters.states || CLEANUP_WHEN_EMPTY,
        filters: statesFilter.map((filter) => ({
          text: INVOICE_LABEL_OVERWRITE[filter] || filter,
          value: filter,
        })),
        render: (_: unknown, record) => (
          <>
            <InvoiceState state={record.worksInvoice.state} />
            <br />
            <span className="color-midnight-lighter">
              {!!record.worksInvoice.due && (
                <small>on {record.worksInvoice.due}</small>
              )}
            </span>
          </>
        ),
      },
      {
        title: 'Organization',
        dataIndex: 'organization',
        render: ({ name }) => name,
        key: 'organization',
        sorter: true,
        filtered: !!organizationsFilter.length,
        filteredValue: filters?.organization || CLEANUP_WHEN_EMPTY,
        filters: [...organizationsFilter]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((organization) => ({
            text: organization.name,
            value: organization.id,
          })),
      },
      {
        title: 'Open Disputes',
        render: (_: unknown, revInvoice) => (
          <div className="color-midnight-lighter">
            <strong>{revInvoice.openedDisputeCount}</strong> Open
          </div>
        ),
      },
      {
        title: 'Action',
        key: 'action',
        render: (_: unknown, revInvoice) => (
          <Link
            to={`/works/supplier/billing/reverse-invoices/${revInvoice.id}`}
          >
            <Button className="ant-block">Review</Button>
          </Link>
        ),
      },
    ];

  const dataSource = reverseInvoicesPage?.values || [];

  const tablePageInfo = useMemo(
    () => ({
      ...pageData,
      showSizeChanger: true,
      defaultPageSize: pageData.limit,
      total: totalInvoices,
    }),
    [pageData, totalInvoices]
  );

  const onFinish = () => setSelectedReverseInvoices([]);

  const handleTableChange: RenderTableOnChange<
    ReverseInvoiceListItemFragmentType
  > = (pagination, filters, sorter) => {
    if (sorter instanceof Array) {
      return;
    }

    const pageSize = pagination.pageSize || tablePageInfo.defaultPageSize;
    const orderDirection = sorter.order === 'ascend' ? 'ASC' : 'DESC';
    const orderMap: Record<string, PaginatedRevInvoiceArg['orderField']> = {
      code: 'CODE',
      amount: 'BILLING_AMOUNT',
      states: 'STATE',
      date: 'CREATED_AT',
      organization: 'ORGANIZATION',
    };
    const orderField: OrderField =
      orderMap[sorter.columnKey as string | number] || defaultOrder;

    if (filters.length) {
      setSelectedReverseInvoices([]);
    }

    setFilters((oldFilters) => ({
      ...oldFilters,
      ...filters,
    }));
    setPageData(() => ({
      orderField,
      orderDirection,
      offset: ((pagination?.current || 1) - 1) * pageSize,
      limit: pageSize,
    }));
  };

  const rowSelection = {
    selectedRowKeys: selectedReverseInvoices.map((invoice) => invoice.id),
    onChange: (_: unknown, records: unknown) => {
      setSelectedReverseInvoices(
        records as ReverseInvoiceListItemFragmentType[]
      );
    },
  };

  const selection = Object.values(selectedReverseInvoices);

  return (
    <div className="table-wrapper">
      <TotalCountLabel
        dataLength={dataSource.length}
        totalCount={totalInvoices}
        label="line items"
      />
      <RenderFilteredTable
        rowKey="id"
        className="reverse-invoices-list"
        dataSource={dataSource}
        columns={revInvoiceColumns}
        loading={loading || filtersLoading}
        onChange={handleTableChange}
        pagination={tablePageInfo}
        filters={filters}
        onClearFilter={onClearFilter}
        onClearAllFilters={onClearAllFilters}
        rowSelection={rowSelection}
      />
      {!!selection.length && (
        <ReverseInvoiceBulkActionFooter
          reverseInvoices={selection}
          onFinish={onFinish}
        />
      )}
      <style jsx>{`
        :global(.reverse-invoices-list .ant-table-row) {
          height: 49px;
        }
      `}</style>
    </div>
  );
};
